import { useTenantSiteCheck } from '@/hooks/admin/useTenantSiteCheck';
import { AppSpin } from '@/components/atoms';
import { ModuleLoader } from '@/runtime/ModuleLoader';
import PortalIdleTimerHandle from '@/components/features/IdleTimerHandle/IdleTimerHandle';
import SsoCompletionPage from '@/features/sso/SsoCompletionPage';
import { MassagerRouterLayout, massagerRoutes } from '@/router/MassagerRouter';
import { SiteAdminRouterLayout, siteAdminRoutes } from '@/router/SiteAdminRouter';
import RezeptPdfProgressPage from '@/pages/main/rezepts/pages/RezeptPdfProgressPage';
import { TenantRouterLayout, tenantRoutes } from '@/router/TenantRouter';
import SiteAdminLoginPage from '@pages/admin/admin-auth/SiteAdminLoginPage';
import ForgotPasswordPage from '@pages/main/auth/ForgotPasswordPage';
import LoginPage from '@pages/main/auth/LoginPage';
import ResetPasswordExpiredPage from '@pages/main/auth/ResetPasswordExpiredPage';
import ResetPasswordPage from '@pages/main/auth/ResetPasswordPage';
import MassagerLoginPage from '@pages/massager/auth/LoginPage';
import MassagerForgotPasswordPage from '@pages/massager/auth/ForgotPasswordPage';
import MassagerResetPasswordPage from '@pages/massager/auth/ResetPasswordPage';
import MassagerResetPasswordExpiredPage from '@pages/massager/auth/ResetPasswordExpiredPage';
import ProtectedMassagerRoute from '@shared/guards/ProtectedMassagerRoute';
import ProtectedRoute from '@shared/guards/ProtectedRoute';
import ProtectedSiteAdminRoute from '@shared/guards/ProtectedSiteAdminRoute';
import { AdminThemeProvider } from '@shared/providers/admin-theme/AdminThemeContext';
import { AuthProvider, useAuth } from '@shared/providers/AuthContext';
import { MassagerAuthProvider } from '@shared/providers/MassagerAuthContext';
import { SiteAdminAuthProvider } from '@shared/providers/SiteAdminAuthContext';
import { TenantProvider } from '@shared/providers/TenantContext';
import { AntdThemeProvider } from '@shared/providers/theme/AntdThemeProvider';
import { ThemeProvider } from '@shared/providers/theme/ThemeContext';
import { ThemeWrapper } from '@shared/providers/theme/ThemeWrapper';
import { resolvePublicPortalProfile } from '@/features/sso/publicPortalProfile';
import { tenantCapability } from '@customize/registry';
import React, { PropsWithChildren } from 'react';
import { Navigate, RouteObject, useParams } from 'react-router-dom';

const PortalConfigurationError = () => (
  <div role="alert" className="min-h-screen flex items-center justify-center bg-gray-100">
    <p className="rounded bg-white p-6 text-red-700">ポータル設定を確認できません。</p>
  </div>
);

const UnifiedPortalLegacyGate: React.FC<PropsWithChildren<{
  destination: 'login' | 'completion' | 'root';
}>> = ({ destination, children }) => {
  const { site } = useParams<{ site: string }>();
  const profile = resolvePublicPortalProfile(site);
  if (profile.mode === 'invalid') return <PortalConfigurationError />;
  if (profile.mode !== 'unified') return <>{children}</>;

  const suffix = destination === 'login'
    ? '/login'
    : destination === 'completion' ? '/sso/complete' : '';
  return <Navigate to={`/${site}${suffix}`} replace />;
};

const TenantSsoCompletionRoute: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const profile = resolvePublicPortalProfile(site);
  if (profile.mode === 'invalid') return <PortalConfigurationError />;
  const entryType = profile.mode === 'unified' ? 'unified' : 'clinic';
  return <SsoCompletionPage entryType={entryType} />;
};

/**
 * The screen is resolved through the module registry so a site type can ship
 * its own login without this route changing: same URL, same providers, same
 * auth flow behind it. `tenant` is passed explicitly because nobody is signed
 * in yet — see `ModuleLoader`. Sites without an override get `auth/Login`'s
 * base registration, which is `LoginPage` itself.
 */
const TenantLoginRoute: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const profile = resolvePublicPortalProfile(site);
  if (profile.mode === 'invalid') return <PortalConfigurationError />;
  const mode = profile.mode === 'unified' ? 'unified' : 'clinic';
  const recoverStaleSessionHydrationViaPassiveSso = tenantCapability(
    site,
    'recoverStaleSessionHydrationViaPassiveSso',
    false,
  );
  return (
    <AuthProvider
      mode={mode}
      recoverStaleSessionHydrationViaPassiveSso={recoverStaleSessionHydrationViaPassiveSso}
    >
      <ThemeProvider>
        <AntdThemeProvider>
          <ModuleLoader
            module="auth"
            component="Login"
            tenant={site}
            // An override arrives as its own chunk; login is the first paint of
            // a session, so the wait gets the same splash the passive-SSO check
            // uses rather than a blank frame.
            fallback={<AppSpin fullscreen size="large" />}
            notFound={<LoginPage />}
          />
        </AntdThemeProvider>
      </ThemeProvider>
    </AuthProvider>
  );
};

/**
 * Inner component that uses the useAuth hook
 * Separated to ensure proper context nesting during rendering and HMR
 */
const TenantThemeWrapper: React.FC<PropsWithChildren> = ({ children }) => {
  const { clinic } = useAuth();

  return (
    <ThemeProvider clinicServiceType={clinic?.service_type}>
      <ProtectedRoute>
        <PortalIdleTimerHandle portal="clinic" />
        <AntdThemeProvider>
          <ThemeWrapper>{children}</ThemeWrapper>
        </AntdThemeProvider>
      </ProtectedRoute>
    </ThemeProvider>
  );
};

const TenantProvidersContent: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <TenantProvider>
      <TenantThemeWrapper>{children}</TenantThemeWrapper>
    </TenantProvider>
  );
};

const TenantProviders: React.FC<PropsWithChildren> = ({ children }) => {
  return <TenantProvidersContent>{children}</TenantProvidersContent>;
};

const TenantAuthBoundary: React.FC<PropsWithChildren> = ({ children }) => {
  const { tenant } = useParams<{ tenant: string }>();
  const profile = resolvePublicPortalProfile(tenant);
  if (profile.mode === 'invalid') return <PortalConfigurationError />;
  const mode = profile.mode === 'unified' ? 'unified' : 'clinic';
  const recoverStaleSessionHydrationViaPassiveSso = tenantCapability(
    tenant,
    'recoverStaleSessionHydrationViaPassiveSso',
    false,
  );
  return (
    <AuthProvider
      mode={mode}
      recoverStaleSessionHydrationViaPassiveSso={recoverStaleSessionHydrationViaPassiveSso}
    >
      {children}
    </AuthProvider>
  );
};

/**
 * Tenant Layout - Wraps tenant-specific content with TenantProvider and ThemeProvider
 */
const TenantLayout: React.FC = () => {
  const { tenant } = useParams<{ tenant: string }>();

  // With this route shape (/:tenant/*), tenant will normally always exist.
  // If it doesn't, fallback safely to root instead of hardcoding a site.
  if (!tenant) {
    return <Navigate to="/" replace />;
  }

  return (
    <TenantProviders>
      <TenantRouterLayout />
    </TenantProviders>
  );
};

/**
 * Massager Layout - Wraps massager (checkin) portal with MassagerAuthProvider and ProtectedMassagerRoute.
 * Strict site_type match enforced by ProtectedMassagerRoute (no family fallback).
 */
const MassagerLayout: React.FC = () => {
  const { site } = useParams<{ site: string }>();

  if (!site) {
    return <Navigate to="/" replace />;
  }
  const profile = resolvePublicPortalProfile(site);
  if (profile.mode === 'invalid') {
    return <PortalConfigurationError />;
  }
  if (profile.mode === 'unified') {
    return <Navigate to={`/${site}`} replace />;
  }

  return (
    <MassagerAuthProvider>
      <ThemeProvider>
        <AntdThemeProvider>
          <ProtectedMassagerRoute>
            <PortalIdleTimerHandle portal="massager" />
            <MassagerRouterLayout />
          </ProtectedMassagerRoute>
        </AntdThemeProvider>
      </ThemeProvider>
    </MassagerAuthProvider>
  );
};

/**
 * Site Admin Layout - Wraps site admin content with SiteAdminAuthProvider and AdminThemeProvider
 */
const LegacySiteAdminLayout: React.FC<{ site: string }> = ({ site }) => {
  const {
    data: siteCheck,
    isLoading: isCheckingSite,
    isError: isSiteCheckError,
  } = useTenantSiteCheck(site, 'admin');

  // Validate site dynamically via API (no hardcoded list)
  if (isCheckingSite) {
    return null;
  }

  if (isSiteCheckError || !siteCheck?.success || !siteCheck.data?.exists) {
    return <Navigate to="/" replace />;
  }

  return (
    <AuthProvider mode="passive">
      <SiteAdminAuthProvider>
        <ProtectedSiteAdminRoute>
          <PortalIdleTimerHandle portal="site-admin" />
          <AdminThemeProvider adminType="site" siteType={site}>
            <ThemeProvider>
              <AntdThemeProvider>
                <SiteAdminRouterLayout />
              </AntdThemeProvider>
            </ThemeProvider>
          </AdminThemeProvider>
        </ProtectedSiteAdminRoute>
      </SiteAdminAuthProvider>
    </AuthProvider>
  );
};

const SiteAdminLayout: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  if (!site) return <Navigate to="/" replace />;

  const profile = resolvePublicPortalProfile(site);
  if (profile.mode === 'invalid') return <PortalConfigurationError />;
  if (profile.mode === 'unified') return <Navigate to={`/${site}`} replace />;

  return <LegacySiteAdminLayout site={site} />;
};

/**
 * Centralized Route Configuration
 *
 * This defines all application routes using the data router API.
 * Routes are organized by portal type: Admin, Site Admin, and Tenant.
 */
export const routes: RouteObject[] = [
  {
    path: '/:site/admin/sso/complete',
    element: (
      <UnifiedPortalLegacyGate destination="completion">
        <SsoCompletionPage entryType="admin" />
      </UnifiedPortalLegacyGate>
    ),
  },
  {
    path: '/:site/sso/complete',
    element: <TenantSsoCompletionRoute />,
  },
  // Site Admin Portal Routes (AdminThemeProvider for theme-aware login UI per site)
  {
    path: '/:site/admin/login',
    element: (
      <UnifiedPortalLegacyGate destination="login">
        <SiteAdminAuthProvider>
          <AdminThemeProvider adminType="site">
            <ThemeProvider>
              <AntdThemeProvider>
                <SiteAdminLoginPage />
              </AntdThemeProvider>
            </ThemeProvider>
          </AdminThemeProvider>
        </SiteAdminAuthProvider>
      </UnifiedPortalLegacyGate>
    ),
  },
  {
    path: '/:site/admin/*',
    element: <SiteAdminLayout />,
    children: siteAdminRoutes,
  },

  // Clinic Portal Routes - ThemeProvider here only for login-page UI (button/link colors by site).
  // After login, user is redirected to /:tenant/* and TenantLayout mounts a separate ThemeProvider
  // with clinicServiceType from auth — so post-login theme is unchanged and not affected.
  {
    path: '/:site/login',
    element: <TenantLoginRoute />,
  },
  {
    path: '/:site/forgot-password',
    element: (
      <AuthProvider>
        <ThemeProvider>
          <AntdThemeProvider>
            <ForgotPasswordPage />
          </AntdThemeProvider>
        </ThemeProvider>
      </AuthProvider>
    ),
  },
  {
    path: '/:site/password/reset/:token',
    element: (
      <AuthProvider>
        <ThemeProvider>
          <AntdThemeProvider>
            <ResetPasswordPage />
          </AntdThemeProvider>
        </ThemeProvider>
      </AuthProvider>
    ),
  },
  {
    path: '/:site/password/reset-expired',
    element: (
      <AuthProvider>
        <ThemeProvider>
          <AntdThemeProvider>
            <ResetPasswordExpiredPage />
          </AntdThemeProvider>
        </ThemeProvider>
      </AuthProvider>
    ),
  },
  {
    path: '/:site/reset-password',
    element: (
      <AuthProvider>
        <ThemeProvider>
          <AntdThemeProvider>
            <ResetPasswordPage />
          </AntdThemeProvider>
        </ThemeProvider>
      </AuthProvider>
    ),
  },

  // Massager Portal Routes (checkin site)
  // - Public login page: /:site/massager/login
  // - Protected branch: /:site/massager/* (requires massager token + strict site_type match)
  {
    path: '/:site/massager/login',
    element: (
      <UnifiedPortalLegacyGate destination="login">
        <MassagerAuthProvider>
          <ThemeProvider>
            <AntdThemeProvider>
              <MassagerLoginPage />
            </AntdThemeProvider>
          </ThemeProvider>
        </MassagerAuthProvider>
      </UnifiedPortalLegacyGate>
    ),
  },
  {
    path: '/:site/massager/password/reset',
    element: (
      <UnifiedPortalLegacyGate destination="login">
        <MassagerAuthProvider>
          <ThemeProvider>
            <AntdThemeProvider>
              <MassagerForgotPasswordPage />
            </AntdThemeProvider>
          </ThemeProvider>
        </MassagerAuthProvider>
      </UnifiedPortalLegacyGate>
    ),
  },
  {
    path: '/:site/massager/password/reset/:token',
    element: (
      <UnifiedPortalLegacyGate destination="login">
        <MassagerAuthProvider>
          <ThemeProvider>
            <AntdThemeProvider>
              <MassagerResetPasswordPage />
            </AntdThemeProvider>
          </ThemeProvider>
        </MassagerAuthProvider>
      </UnifiedPortalLegacyGate>
    ),
  },
  {
    path: '/:site/massager/password/reset-expired',
    element: (
      <UnifiedPortalLegacyGate destination="login">
        <MassagerAuthProvider>
          <ThemeProvider>
            <AntdThemeProvider>
              <MassagerResetPasswordExpiredPage />
            </AntdThemeProvider>
          </ThemeProvider>
        </MassagerAuthProvider>
      </UnifiedPortalLegacyGate>
    ),
  },
  {
    path: '/:site/massager/*',
    element: <MassagerLayout />,
    children: massagerRoutes,
  },

  // Standalone popup for rezept PDF progress (no layout — bare page)
  {
    path: '/:site/rezepts/pdf-progress',
    element: <RezeptPdfProgressPage />,
  },

  // Tenant Routes (Protected)
  {
    path: '/:tenant/*',
    element: (
      <TenantAuthBoundary>
        <TenantLayout />
      </TenantAuthBoundary>
    ),
    children: tenantRoutes,
  },

  // Default Redirect
  {
    path: '/',
    element: <Navigate to="/medix/login" replace />,
  },
];
